/**
 * RoLoading - Loading 加载组件
 * 
 * 功能特性：
 * - 支持指令式绑定 (ro-loading)
 * - 支持服务调用 (RoLoading.service)
 * - 支持自定义文字、颜色、尺寸（large | medium | small）
 * - 支持全屏模式和局部模式
 * - 主色调默认为 #161616
 *
 * 尺寸（ro-loading-size / options.size）：
 * - large（默认）：图标 42×42px，文案 14px
 * - medium：图标 24×24px，文案 12px
 * - small：图标 20×20px，文案 12px
 *
 * 使用方法：
 * 1. 指令式：
 *    <div ro-loading="true" ro-loading-text="加载中..." ro-loading-body></div>
 * 
 * 2. 服务式：
 *    const loading = RoLoading.service({
 *      text: 'Loading...',
 *      color: '#161616',
 *      fullscreen: true
 *    });
 *    loading.close();
 */

class RoLoading {
  /** @returns {'large'|'medium'|'small'} */
  static _normalizeSize(raw) {
    const size = String(raw ?? 'large').trim().toLowerCase();
    if (size === 'medium' || size === 'small') return size;
    return 'large';
  }

  /** 从 mask 移除旧尺寸类并应用新档位 */
  static _applySizeClass(mask, size) {
    if (!mask) return;
    mask.classList.remove('ro-loading-size-large', 'ro-loading-size-medium', 'ro-loading-size-small');
    mask.classList.add(`ro-loading-size-${RoLoading._normalizeSize(size)}`);
  }

  static get STYLES() {
    return `
:root {
  --ro-loading-main-color: #161616;
  --ro-loading-mask-bg: rgba(255, 255, 255, 0.9);
  --ro-loading-z-index: 2000;
}
.ro-loading-mask {
  position: absolute;
  z-index: var(--ro-loading-z-index);
  background-color: var(--ro-loading-mask-bg);
  margin: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transition: opacity 0.3s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
}
.ro-loading-mask.is-fullscreen {
  position: fixed;
}
.ro-loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.ro-loading-spinner .circular {
  height: 42px;
  width: 42px;
  animation: loading-rotate 2s linear infinite;
}
.ro-loading-spinner .path {
  animation: loading-dash 1.5s ease-in-out infinite;
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
  stroke-width: 2;
  stroke: var(--ro-loading-main-color);
  stroke-linecap: round;
}
.ro-loading-text {
  color: var(--ro-loading-main-color);
  margin: 12px 0;
  font-size: 14px;
}
/* medium：24×24 图标，12px 文案 */
.ro-loading-mask.ro-loading-size-medium .ro-loading-spinner .circular {
  width: 24px;
  height: 24px;
}
.ro-loading-mask.ro-loading-size-medium .ro-loading-spinner .path {
  stroke-width: 1.5;
}
.ro-loading-mask.ro-loading-size-medium .ro-loading-text {
  margin: 8px 0;
  font-size: 12px;
}
/* small：20×20 图标，12px 文案 */
.ro-loading-mask.ro-loading-size-small .ro-loading-spinner .circular {
  width: 20px;
  height: 20px;
}
.ro-loading-mask.ro-loading-size-small .ro-loading-spinner .path {
  stroke-width: 1.5;
}
.ro-loading-mask.ro-loading-size-small .ro-loading-text {
  margin: 8px 0;
  font-size: 12px;
}
@keyframes loading-rotate {
  100% { transform: rotate(360deg); }
}
@keyframes loading-dash {
  0% { stroke-dasharray: 1, 200; stroke-dashoffset: 0; }
  50% { stroke-dasharray: 90, 150; stroke-dashoffset: -40px; }
  100% { stroke-dasharray: 90, 150; stroke-dashoffset: -120px; }
}
.ro-loading-fade-enter,
.ro-loading-fade-leave-active {
  opacity: 0;
}
.ro-loading-parent--relative {
  position: relative !important;
}
.ro-loading-parent--hidden {
  overflow: hidden !important;
}
    `;
  }

  /**
   * 注入样式
   * @private
   */
  static _injectStyles() {
    if (typeof document === 'undefined') return;
    const id = 'ro-loading-styles';
    const existing = document.getElementById(id);
    if (existing) {
      existing.textContent = RoLoading.STYLES;
      return;
    }
    const style = document.createElement('style');
    style.id = id;
    style.textContent = RoLoading.STYLES;
    document.head.appendChild(style);
  }

  /**
   * 构造函数
   * @param {Object} options 配置项
   */
  constructor(options = {}) {
    RoLoading._injectStyles();
    this.options = {
      target: document.body,
      text: null,
      color: '#161616',
      fullscreen: false,
      body: false,
      size: 'large',
      ...options
    };
    this.options.size = RoLoading._normalizeSize(this.options.size);

    this.instance = null;
    this.visible = false;
    this._init();
  }

  /**
   * 初始化加载实例
   * @private
   */
  _init() {
    const { text, color, fullscreen, body, size } = this.options;
    
    // 创建遮罩层
    const mask = document.createElement('div');
    mask.className = 'ro-loading-mask';
    RoLoading._applySizeClass(mask, size);
    if (fullscreen || body) {
      mask.classList.add('is-fullscreen');
    }

    // 设置自定义颜色
    if (color) {
      mask.style.setProperty('--ro-loading-main-color', color);
    }

    // 创建旋转器
    const spinner = document.createElement('div');
    spinner.className = 'ro-loading-spinner';
    spinner.innerHTML = `
      <svg class="circular" viewBox="25 25 50 50">
        <circle class="path" cx="50" cy="50" r="20" fill="none"/>
      </svg>
    `;

    // 创建文字
    if (text) {
      const textEl = document.createElement('p');
      textEl.className = 'ro-loading-text';
      textEl.textContent = text;
      spinner.appendChild(textEl);
    }

    mask.appendChild(spinner);
    this.instance = mask;
  }

  /**
   * 显示加载层
   */
  open() {
    if (this.visible) return;
    const { fullscreen, body, target } = this.options;
    const actualTarget = (fullscreen || body) ? document.body : target;
    
    // 如果不是全屏且不是插入 body，需要确保父元素是 relative
    if (!fullscreen && !body && actualTarget !== document.body) {
      const style = window.getComputedStyle(actualTarget);
      if (style.position === 'static') {
        actualTarget.classList.add('ro-loading-parent--relative');
      }
    }

    if (fullscreen || body) {
      document.body.classList.add('ro-loading-parent--hidden');
    }

    actualTarget.appendChild(this.instance);
    this.visible = true;
  }

  /**
   * 关闭加载层
   */
  close() {
    if (!this.visible) return;
    if (this.instance && this.instance.parentNode) {
      this.instance.parentNode.removeChild(this.instance);
    }
    this.visible = false;

    const { fullscreen, body, target } = this.options;
    if (fullscreen || body) {
      document.body.classList.remove('ro-loading-parent--hidden');
    } else if (target) {
      target.classList.remove('ro-loading-parent--relative');
    }
  }

  // --- 静态方法与服务支持 ---

  static _serviceInstance = null;

  /**
   * 以服务方式调用 Loading
   * @param {Object} options 配置项
   * @returns {Object} 包含 close 方法的实例
   */
  static service(options = {}) {
    // 如果已经存在全局实例，先关闭
    if (RoLoading._serviceInstance) {
      RoLoading._serviceInstance.close();
    }

    const instance = new RoLoading({
      fullscreen: true, // 服务调用默认全屏
      ...options
    });

    instance.open();
    RoLoading._serviceInstance = instance;

    return {
      close: () => {
        instance.close();
        if (RoLoading._serviceInstance === instance) {
          RoLoading._serviceInstance = null;
        }
      }
    };
  }

  /**
   * 初始化指令式监听
   * 监听 ro-loading 属性的变化
   */
  static initDirective() {
    if (typeof document === 'undefined') return;
    
    const init = () => {
      if (!document.body) {
        setTimeout(init, 50);
        return;
      }
      
      RoLoading._injectStyles();
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes') {
            const attr = mutation.attributeName;
            if (attr === 'ro-loading' ||
                attr === 'ro-loading-text' ||
                attr === 'ro-loading-color' ||
                attr === 'ro-loading-body' ||
                attr === 'ro-loading-size') {
              RoLoading._handleDirective(mutation.target);
            }
          }
        });
      });

      observer.observe(document.body, {
        attributes: true,
        subtree: true,
        attributeFilter: ['ro-loading', 'ro-loading-text', 'ro-loading-color', 'ro-loading-body', 'ro-loading-size']
      });

      // 初始扫描
      document.querySelectorAll('[ro-loading]').forEach(el => {
        RoLoading._handleDirective(el);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  /**
   * 处理单个元素的指令逻辑
   * @private
   * @param {HTMLElement} el 
   */
  static _handleDirective(el) {
    try {
      const isLoading = el.getAttribute('ro-loading') === 'true';
      let instance = el._roLoadingInstance;

      if (isLoading) {
        const text = el.getAttribute('ro-loading-text');
        const color = el.getAttribute('ro-loading-color') || '#161616';
        const body = el.hasAttribute('ro-loading-body');
        const size = RoLoading._normalizeSize(el.getAttribute('ro-loading-size'));

        if (!instance) {
          instance = new RoLoading({
            target: el,
            text,
            color,
            body,
            size
          });
          el._roLoadingInstance = instance;
        } else {
          // 如果实例已存在，更新其配置
          instance.options.text = text;
          instance.options.color = color;
          instance.options.body = body;
          instance.options.size = size;

          // 更新 DOM
          const mask = instance.instance;
          if (mask) {
            RoLoading._applySizeClass(mask, size);
            // 更新文字
            let textEl = mask.querySelector('.ro-loading-text');
            if (text) {
              if (!textEl) {
                textEl = document.createElement('p');
                textEl.className = 'ro-loading-text';
                mask.querySelector('.ro-loading-spinner').appendChild(textEl);
              }
              textEl.textContent = text;
            } else if (textEl) {
              textEl.remove();
            }
            
            // 更新颜色
            mask.style.setProperty('--ro-loading-main-color', color);
            
            // 更新 body 模式
            if (body) {
              mask.classList.add('is-fullscreen');
            } else {
              mask.classList.remove('is-fullscreen');
            }
          }
        }
        instance.open();
      } else if (instance) {
        instance.close();
      }
    } catch (e) {
      console.error('RoLoading directive error:', e);
    }
  }
}

// 自动初始化指令监听
if (typeof window !== 'undefined') {
  RoLoading.initDirective();
}

export default RoLoading;
